import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StatusToast from './StatusToast';
import { adminApi } from './api';
import ModuleQuickCreate from './ModuleQuickCreate';
import BulkCatalogImport from './BulkCatalogImport';
import useAdminConfirm from './useAdminConfirm';

const PAGE_SIZE = 30;

const TYPE_OPTIONS = [
  ['fiche', 'Fiche de révision'],
  ['document', 'Document'],
  ['pdf', 'PDF'],
];

const FILTER_TYPE_OPTIONS = [
  ['', 'Tous les types'],
  ...TYPE_OPTIONS,
  ['cours', 'Cours (historique)'],
];

const LEVEL_OPTIONS = [
  ['base', 'Base'],
  ['intermediaire', 'Intermédiaire'],
  ['avance', 'Avancé'],
];

const ACCESS_OPTIONS = [
  ['free', 'Free'],
  ['premium', 'Premium'],
];

const STATUS_OPTIONS = [
  ['', 'Tous les états'],
  ['draft', 'Brouillon'],
  ['published', 'Publié'],
  ['archived', 'Archivé'],
];

const STATUS_LABELS = {
  draft: 'Brouillon',
  published: 'Publié',
  archived: 'Archivé',
};

function typeLabel(value) {
  return (
    FILTER_TYPE_OPTIONS.find(([type]) => type === value)?.[1] ||
    value ||
    '—'
  );
}

function levelLabel(value) {
  return LEVEL_OPTIONS.find(([level]) => level === value)?.[1] || value || '—';
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleString('fr-FR')
    : '—';
}

function formatBytes(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function htmlToEditableText(value) {
  if (!value) return '';
  return String(value)
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

function StatusBadge({ status }) {
  return (
    <span className={`bo-badge bo-badge--${status || 'draft'}`}>
      {STATUS_LABELS[status] || status || '—'}
    </span>
  );
}

function AccessBadge({ tier }) {
  return (
    <span
      className={`bo-badge ${
        tier === 'premium' ? 'bo-badge--premium' : 'bo-badge--free'
      }`}
    >
      {tier === 'premium' ? 'Premium' : 'Free'}
    </span>
  );
}

function SortButton({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <button className="bo-sort-button" onClick={() => onSort(sortKey)}>
      {label}
      <span
        className={`bo-sort-indicator ${
          active ? 'bo-sort-indicator--active' : ''
        }`}
      >
        {active ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );
}

function emptyCreateForm() {
  return {
    module_id: '',
    titre: '',
    description: '',
    type_ressource: 'document',
    access_tier: 'free',
    level: 'base',
    duration_minutes: '10',
    url: '',
    content_text: '',
    tags: '',
  };
}

function editorFromResource(resource) {
  return {
    module_id: resource?.module_id ? String(resource.module_id) : '',
    titre: resource?.titre || '',
    description: resource?.description || '',
    type_ressource:
      TYPE_OPTIONS.some(([value]) => value === resource?.type_ressource)
        ? resource.type_ressource
        : 'document',
    access_tier: resource?.access_tier || 'free',
    level: resource?.level || 'base',
    duration_minutes:
      resource?.duration_minutes === null ||
      resource?.duration_minutes === undefined
        ? ''
        : String(resource.duration_minutes),
    url: resource?.url || '',
    content_text: htmlToEditableText(resource?.content_html),
    tags: Array.isArray(resource?.tags) ? resource.tags.join(', ') : '',
  };
}

export default function ResourceManager({
  catalog,
  refresh,
  runAction,
  onUnauthorized,
}) {
  const modules = catalog?.modules || [];
  const activeModules = modules.filter((m) => m.is_active !== false);
  const [formOpen, setFormOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [filters, setFilters] = useState({
    search: '',
    moduleId: '',
    type: '',
    access: '',
    status: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [sort, setSort] = useState({ key: 'updated_at', direction: 'desc' });
  const [offset, setOffset] = useState(0);
  const [result, setResult] = useState({ items: [], total: 0 });
  const [loadingList, setLoadingList] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editor, setEditor] = useState(emptyCreateForm);
  const [initialContentText, setInitialContentText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const { askConfirm, confirmDialog } = useAdminConfirm();
  const gridRef = useRef(null);
  const detailRef = useRef(null);
  const pendingScrollRef = useRef(false);

  const loadList = useCallback(
    async (requestedOffset = offset) => {
      try {
        setLoadingList(true);
        setError('');
        const next = await adminApi.resources({
          ...appliedFilters,
          sort: sort.key,
          order: sort.direction,
          limit: PAGE_SIZE,
          offset: requestedOffset,
        });
        setResult(next || { items: [], total: 0 });

        const items = next?.items || [];
        if (!items.length) {
          setSelectedId(null);
          setDetail(null);
        } else if (
          !selectedId ||
          !items.some((item) => Number(item.id) === Number(selectedId))
        ) {
          setSelectedId(items[0].id);
          pendingScrollRef.current = false;
        }
      } catch (loadError) {
        if (loadError.status === 401) onUnauthorized?.();
        else setError(loadError.message || 'Chargement des ressources impossible.');
      } finally {
        setLoadingList(false);
      }
    },
    [
      appliedFilters,
      offset,
      onUnauthorized,
      selectedId,
      sort.direction,
      sort.key,
    ]
  );

  const loadDetail = useCallback(
    async (id) => {
      if (!id) {
        setDetail(null);
        return;
      }
      try {
        setLoadingDetail(true);
        setError('');
        const next = await adminApi.resource(id);
        setDetail(next);
        const nextEditor = editorFromResource(next);
        setEditor(nextEditor);
        setInitialContentText(nextEditor.content_text);
        setPreviewHtml('');
        if (
          pendingScrollRef.current &&
          detailRef.current?.scrollIntoView
        ) {
          pendingScrollRef.current = false;
          requestAnimationFrame(() => {
            detailRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          });
        }
      } catch (loadError) {
        if (loadError.status === 401) onUnauthorized?.();
        else setError(loadError.message || 'Chargement de la ressource impossible.');
      } finally {
        setLoadingDetail(false);
      }
    },
    [onUnauthorized]
  );

  useEffect(() => {
    loadList(offset);
  }, [offset, appliedFilters, sort.key, sort.direction]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const canPreview = Boolean(
    detail?.storage_key || detail?.url || detail?.content_html
  );
  const isDraft = detail?.publication_status === 'draft';

  function applyFilters(event) {
    event?.preventDefault?.();
    setAppliedFilters({ ...filters });
    setOffset(0);
  }

  function resetFilters() {
    const next = {
      search: '',
      moduleId: '',
      type: '',
      access: '',
      status: '',
    };
    setFilters(next);
    setAppliedFilters(next);
    setOffset(0);
  }

  function changeSort(key) {
    setSort((current) => ({
      key,
      direction:
        current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
    setOffset(0);
  }

  function openResource(id, scroll = true) {
    pendingScrollRef.current = scroll;
    setSelectedId(id);
    if (Number(id) === Number(selectedId)) {
      loadDetail(id);
    }
  }

  async function createResource(event) {
    event.preventDefault();
    setError('');
    setNotice('');

    let created = null;
    const ok = await runAction(
      async () => {
        created = await adminApi.createResource({
          module_id: Number(createForm.module_id),
          titre: createForm.titre,
          description: createForm.description || null,
          type_ressource: createForm.type_ressource,
          access_tier: createForm.access_tier,
          publication_status: 'draft',
          level: createForm.level || null,
          duration_minutes:
            createForm.duration_minutes === ''
              ? null
              : Number(createForm.duration_minutes),
          url: createForm.url || null,
          content_text: createForm.content_text || null,
          tags: createForm.tags
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean),
        });
      },
      'Ressource créée en brouillon.'
    );

    if (!ok || !created) return;

    setCreateForm(emptyCreateForm());
    setFormOpen(false);
    setNotice('Ressource créée en brouillon.');
    setAppliedFilters({
      search: '',
      moduleId: '',
      type: '',
      access: '',
      status: '',
    });
    setFilters({
      search: '',
      moduleId: '',
      type: '',
      access: '',
      status: '',
    });
    setOffset(0);
    setSelectedId(created.id);
    pendingScrollRef.current = true;
    await Promise.all([loadList(0), refresh?.()]);
  }

  async function saveResource(event) {
    event.preventDefault();
    if (!detail || !isDraft) return;

    const payload = {
      module_id: Number(editor.module_id),
      titre: editor.titre,
      description: editor.description || null,
      type_ressource: editor.type_ressource,
      access_tier: editor.access_tier,
      level: editor.level || null,
      duration_minutes:
        editor.duration_minutes === ''
          ? null
          : Number(editor.duration_minutes),
      url: editor.url || null,
      tags: editor.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
    };

    if (editor.content_text !== initialContentText) {
      payload.content_text = editor.content_text || null;
    }

    const ok = await runAction(
      () => adminApi.updateResource(detail.id, payload),
      'Ressource mise à jour.'
    );
    if (!ok) return;

    setNotice('Ressource mise à jour.');
    await Promise.all([loadDetail(detail.id), loadList(offset), refresh?.()]);
  }

  async function updateAccess(value) {
    if (!detail) return;
    if (
      detail.publication_status === 'published' &&
      !(await askConfirm({
        title: 'Modifier l’accès de la ressource ?',
        description: `Cette ressource est déjà publiée. Son accès passera immédiatement à ${value === 'premium' ? 'Premium' : 'Free'}.`,
        confirmLabel: 'Modifier l’accès',
      }))
    ) return;

    const ok = await runAction(
      () => adminApi.updateResource(detail.id, { access_tier: value }),
      'Accès de la ressource mis à jour.'
    );
    if (!ok) return;
    setNotice('Accès de la ressource mis à jour.');
    await Promise.all([loadDetail(detail.id), loadList(offset), refresh?.()]);
  }

  async function changePublication(nextStatus) {
    if (!detail) return;

    if (nextStatus === 'published' && !(await askConfirm({
      title: 'Publier cette ressource ?',
      description: 'Elle deviendra visible aux candidats autorisés selon son niveau d’accès.',
      confirmLabel: 'Publier la ressource',
    }))) return;

    let action;
    if (nextStatus === 'archived') {
      if (!(await askConfirm({
        title: 'Archiver cette ressource ?',
        description: 'La ressource ne sera plus visible par les candidats. Son contenu sera conservé.',
        confirmLabel: 'Archiver la ressource',
        intent: 'danger',
      }))) return;
      action = () => adminApi.archiveResource(detail.id);
    } else {
      action = () =>
        adminApi.updateResource(detail.id, {
          publication_status: nextStatus,
        });
    }

    const labels = {
      draft: 'Ressource remise en brouillon.',
      published: 'Ressource publiée.',
      archived: 'Ressource archivée.',
    };

    const ok = await runAction(action, labels[nextStatus]);
    if (!ok) return;
    setNotice(labels[nextStatus]);
    await Promise.all([loadDetail(detail.id), loadList(offset), refresh?.()]);
  }

  async function uploadFile(file) {
    if (!detail || !file || !isDraft) return;
    try {
      setUploading(true);
      setError('');
      setNotice('');
      const metadata = await adminApi.uploadResourceFile(file);
      await adminApi.attachResourceFile(detail.id, metadata);
      setNotice(
        detail.storage_key
          ? 'Fichier remplacé.'
          : 'Fichier associé à la ressource.'
      );
      await Promise.all([loadDetail(detail.id), loadList(offset), refresh?.()]);
    } catch (uploadError) {
      if (uploadError.status === 401) onUnauthorized?.();
      else setError(uploadError.message || 'Envoi du fichier impossible.');
    } finally {
      setUploading(false);
    }
  }

  async function previewResource() {
    if (!detail || !canPreview) return;
    try {
      setError('');
      setPreviewHtml('');
      const preview = await adminApi.previewResource(detail.id);
      if (preview.kind === 'html') {
        setPreviewHtml(preview.contentHtml || '');
        return;
      }
      if (preview.url) {
        window.open(preview.url, '_blank', 'noopener,noreferrer');
      }
    } catch (previewError) {
      if (previewError.status === 401) onUnauthorized?.();
      else setError(previewError.message || 'Prévisualisation impossible.');
    }
  }

  const contentSummary = useMemo(() => {
    if (!detail) return 'Aucun contenu';
    const parts = [];
    if (detail.storage_key) parts.push('fichier stocké');
    if (detail.url) parts.push('URL externe');
    if (detail.content_html) parts.push('contenu texte');
    return parts.length ? parts.join(' + ') : 'Aucun contenu';
  }, [detail]);

  return (
    <div className="bo-stack">
      {confirmDialog}
      <header className="bo-panel__header bo-resource-page-header">
        <div>
          <p className="bo-eyebrow">Contenus</p>
          <h2>Ressources</h2>
          <p className="bo-muted">
            Catalogue éditorial stocké côté serveur. Les fichiers ne sont jamais
            embarqués dans l’application cliente.
          </p>
        </div>
        <button
          className="bo-button bo-button--primary"
          onClick={() => setFormOpen((current) => !current)}
        >
          {formOpen ? 'Fermer' : '+ Nouvelle ressource'}
        </button>
      </header>

      {error && <div className="bo-alert bo-alert--error">{error}</div>}
      <BulkCatalogImport kind="resources" onImported={async () => { await loadList(0); await refresh?.(); }} />}
      <StatusToast message={notice} />

      {formOpen && (
        <section className="bo-panel">
          <div className="bo-panel__header">
            <div>
              <h3>Nouvelle ressource</h3>
              <p className="bo-muted">
                Toute nouvelle ressource est créée en brouillon avant validation
                et publication.
              </p>
            </div>
            <StatusBadge status="draft" />
          </div>

          <form className="bo-form bo-resource-editor" onSubmit={createResource}>
            <label>
              Matière
              <select
                className="bo-input"
                value={createForm.module_id}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    module_id: event.target.value,
                  }))
                }
                required
              >
                <option value="">Choisir…</option>
                {activeModules.map((module) => (
                  <option key={module.id} value={module.id}>
                    {module.matiere}
                  </option>
                ))}
              </select>
            </label>
            <div className="bo-span-2">
              <ModuleQuickCreate onCreated={async (module) => {
                setCreateForm((current) => ({ ...current, module_id: String(module.id) }));
                await refresh?.();
              }} />
            </div>
            <label>
              Titre
              <input
                className="bo-input"
                value={createForm.titre}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    titre: event.target.value,
                  }))
                }
                required
              />
            </label>
            <label>
              Nature
              <select
                className="bo-input"
                value={createForm.type_ressource}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    type_ressource: event.target.value,
                  }))
                }
              >
                {TYPE_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Accès
              <select
                className="bo-input"
                value={createForm.access_tier}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    access_tier: event.target.value,
                  }))
                }
              >
                {ACCESS_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Niveau
              <select
                className="bo-input"
                value={createForm.level}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    level: event.target.value,
                  }))
                }
              >
                {LEVEL_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Durée estimée (min)
              <input
                className="bo-input"
                type="number"
                min="0"
                value={createForm.duration_minutes}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    duration_minutes: event.target.value,
                  }))
                }
              />
            </label>
            <label className="bo-span-2">
              Description
              <textarea
                className="bo-input"
                rows="3"
                value={createForm.description}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              URL externe (facultatif)
              <input
                className="bo-input"
                value={createForm.url}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    url: event.target.value,
                  }))
                }
                placeholder="https://…"
              />
            </label>
            <label>
              Tags séparés par virgules
              <input
                className="bo-input"
                value={createForm.tags}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    tags: event.target.value,
                  }))
                }
              />
            </label>
            <label className="bo-span-2">
              Contenu texte (facultatif)
              <textarea
                className="bo-input"
                rows="5"
                value={createForm.content_text}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    content_text: event.target.value,
                  }))
                }
              />
            </label>
            <div className="bo-actions bo-span-2">
              <button
                type="button"
                className="bo-button bo-button--ghost"
                onClick={() => {
                  setCreateForm(emptyCreateForm());
                  setFormOpen(false);
                }}
              >
                Annuler
              </button>
              <button className="bo-button bo-button--primary">
                Créer en brouillon
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="bo-panel" ref={gridRef}>
        <div className="bo-panel__header">
          <div>
            <h3>Catalogue</h3>
            <p className="bo-muted">
              {result.total || 0} ressource(s) correspondant aux critères.
            </p>
          </div>
          <span className="bo-kpi">
            {loadingList ? 'Chargement…' : `${result.total || 0} résultat(s)`}
          </span>
        </div>

        <form className="bo-resource-filters" onSubmit={applyFilters}>
          <label>
            Recherche
            <input
              className="bo-input"
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
              placeholder="Titre, description, matière…"
            />
          </label>
          <label>
            Matière
            <select
              className="bo-input"
              value={filters.moduleId}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  moduleId: event.target.value,
                }))
              }
            >
              <option value="">Toutes</option>
              {activeModules.map((module) => (
                <option key={module.id} value={module.id}>
                  {module.matiere}
                </option>
              ))}
            </select>
          </label>
          <label>
            Type
            <select
              className="bo-input"
              value={filters.type}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  type: event.target.value,
                }))
              }
            >
              {FILTER_TYPE_OPTIONS.map(([value, label]) => (
                <option key={value || 'all'} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Accès
            <select
              className="bo-input"
              value={filters.access}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  access: event.target.value,
                }))
              }
            >
              <option value="">Tous</option>
              {ACCESS_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Publication
            <select
              className="bo-input"
              value={filters.status}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  status: event.target.value,
                }))
              }
            >
              {STATUS_OPTIONS.map(([value, label]) => (
                <option key={value || 'all'} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <div className="bo-actions bo-resource-filter-actions">
            <button className="bo-button bo-button--primary">Rechercher</button>
            <button
              type="button"
              className="bo-button bo-button--ghost"
              onClick={resetFilters}
            >
              Réinitialiser
            </button>
          </div>
        </form>

        {loadingList && !(result.items || []).length ? (
          <div className="bo-loading">Chargement…</div>
        ) : !(result.items || []).length ? (
          <div className="bo-empty">Aucune ressource.</div>
        ) : (
          <div className="bo-table-wrap bo-resource-grid-wrap">
            <table className="bo-table bo-resource-grid">
              <thead>
                <tr>
                  <th>
                    <SortButton
                      label="Ressource"
                      sortKey="title"
                      sort={sort}
                      onSort={changeSort}
                    />
                  </th>
                  <th>
                    <SortButton
                      label="Matière"
                      sortKey="module"
                      sort={sort}
                      onSort={changeSort}
                    />
                  </th>
                  <th>
                    <SortButton
                      label="Type"
                      sortKey="type"
                      sort={sort}
                      onSort={changeSort}
                    />
                  </th>
                  <th>
                    <SortButton
                      label="Accès"
                      sortKey="access"
                      sort={sort}
                      onSort={changeSort}
                    />
                  </th>
                  <th>
                    <SortButton
                      label="Publication"
                      sortKey="status"
                      sort={sort}
                      onSort={changeSort}
                    />
                  </th>
                  <th>Fichier</th>
                  <th>
                    <SortButton
                      label="Mise à jour"
                      sortKey="updated_at"
                      sort={sort}
                      onSort={changeSort}
                    />
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {(result.items || []).map((resource) => {
                  const selected = Number(resource.id) === Number(selectedId);
                  return (
                    <tr
                      key={resource.id}
                      className={selected ? 'is-selected' : ''}
                      tabIndex="0"
                      aria-selected={selected}
                      onClick={() => openResource(resource.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openResource(resource.id);
                        }
                      }}
                    >
                      <td>
                        <strong>{resource.titre}</strong>
                        <small className="bo-cell-ellipsis">
                          {resource.description || 'Sans description'}
                        </small>
                      </td>
                      <td>{resource.module_title || 'Non rattachée'}</td>
                      <td>{typeLabel(resource.type_ressource)}</td>
                      <td>
                        <AccessBadge tier={resource.access_tier} />
                      </td>
                      <td>
                        <StatusBadge status={resource.publication_status} />
                      </td>
                      <td>
                        {resource.file_name ? (
                          <span
                            className="bo-cell-ellipsis"
                            title={resource.file_name}
                          >
                            {resource.file_name}
                          </span>
                        ) : (
                          <span className="bo-muted">—</span>
                        )}
                      </td>
                      <td>{formatDate(resource.updated_at)}</td>
                      <td>
                        <button
                          className="bo-button bo-button--ghost bo-button--compact"
                          onClick={(event) => {
                            event.stopPropagation();
                            openResource(resource.id);
                          }}
                        >
                          Ouvrir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="bo-row bo-row--between bo-pagination">
          <small className="bo-muted">
            {result.total
              ? `${offset + 1}–${Math.min(
                  offset + (result.items || []).length,
                  result.total
                )} sur ${result.total}`
              : '0 ressource'}
          </small>
          <div className="bo-actions">
            <button
              className="bo-button bo-button--ghost"
              disabled={loadingList || offset === 0}
              onClick={() =>
                setOffset((current) => Math.max(0, current - PAGE_SIZE))
              }
            >
              Précédent
            </button>
            <button
              className="bo-button bo-button--ghost"
              disabled={
                loadingList ||
                offset + (result.items || []).length >= (result.total || 0)
              }
              onClick={() => setOffset((current) => current + PAGE_SIZE)}
            >
              Suivant
            </button>
          </div>
        </div>
      </section>

      <section className="bo-panel bo-resource-detail" ref={detailRef}>
        {!selectedId ? (
          <div className="bo-empty">
            Sélectionnez une ressource pour ouvrir sa fiche éditoriale.
          </div>
        ) : loadingDetail && !detail ? (
          <div className="bo-loading">Chargement de la ressource…</div>
        ) : detail ? (
          <div className="bo-stack">
            <div className="bo-panel__header">
              <div>
                <p className="bo-eyebrow">Ressource #{detail.id}</p>
                <h3>{detail.titre}</h3>
                <p className="bo-muted">
                  {detail.module_title || 'Non rattachée'} ·{' '}
                  {typeLabel(detail.type_ressource)} · {contentSummary}
                </p>
              </div>
              <div className="bo-inline-controls">
                <StatusBadge status={detail.publication_status} />
                <select
                  className="bo-input bo-select"
                  value={detail.access_tier}
                  onChange={(event) => updateAccess(event.target.value)}
                >
                  {ACCESS_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bo-resource-lifecycle">
              <div>
                <strong>Cycle éditorial</strong>
                <p className="bo-muted">
                  Les modifications de contenu et de fichier se font uniquement
                  en brouillon.
                </p>
              </div>
              <div className="bo-actions">
                {detail.publication_status === 'draft' && (
                  <>
                    <button
                      className="bo-button bo-button--primary"
                      onClick={() => changePublication('published')}
                    >
                      Publier
                    </button>
                    <button
                      className="bo-button bo-button--danger-ghost"
                      onClick={() => changePublication('archived')}
                    >
                      Archiver
                    </button>
                  </>
                )}
                {detail.publication_status === 'published' && (
                  <>
                    <button
                      className="bo-button bo-button--ghost"
                      onClick={() => changePublication('draft')}
                    >
                      Repasser en brouillon
                    </button>
                    <button
                      className="bo-button bo-button--danger-ghost"
                      onClick={() => changePublication('archived')}
                    >
                      Archiver
                    </button>
                  </>
                )}
                {detail.publication_status === 'archived' && (
                  <button
                    className="bo-button bo-button--primary"
                    onClick={() => changePublication('draft')}
                  >
                    Restaurer en brouillon
                  </button>
                )}
              </div>
            </div>

            <form className="bo-form bo-resource-editor" onSubmit={saveResource}>
              <label>
                Matière
                <select
                  className="bo-input"
                  value={editor.module_id}
                  disabled={!isDraft}
                  onChange={(event) =>
                    setEditor((current) => ({
                      ...current,
                      module_id: event.target.value,
                    }))
                  }
                  required
                >
                  <option value="">Choisir…</option>
                  {activeModules.map((module) => (
                    <option key={module.id} value={module.id}>
                      {module.matiere}
                    </option>
                  ))}
                  {detail?.module_id &&
                    !activeModules.some((m) => m.id === detail.module_id) && (
                      <option value={detail.module_id}>
                        {detail.module_title || 'Matière désactivée'} (désactivée)
                      </option>
                    )}
                </select>
              </label>
              {isDraft && <div className="bo-span-2">
                <ModuleQuickCreate onCreated={async (module) => {
                  setEditor((current) => ({ ...current, module_id: String(module.id) }));
                  await refresh?.();
                }} />
              </div>}
              <label>
                Titre
                <input
                  className="bo-input"
                  value={editor.titre}
                  disabled={!isDraft}
                  onChange={(event) =>
                    setEditor((current) => ({
                      ...current,
                      titre: event.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label>
                Nature
                <select
                  className="bo-input"
                  value={editor.type_ressource}
                  disabled={!isDraft}
                  onChange={(event) =>
                    setEditor((current) => ({
                      ...current,
                      type_ressource: event.target.value,
                    }))
                  }
                >
                  {TYPE_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                {detail.type_ressource === 'cours' && (
                  <small className="bo-muted">
                    Type historique : choisissez un type actuel avant le prochain
                    enregistrement.
                  </small>
                )}
              </label>
              <label>
                Niveau
                <select
                  className="bo-input"
                  value={editor.level}
                  disabled={!isDraft}
                  onChange={(event) =>
                    setEditor((current) => ({
                      ...current,
                      level: event.target.value,
                    }))
                  }
                >
                  {LEVEL_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Durée estimée (min)
                <input
                  className="bo-input"
                  type="number"
                  min="0"
                  value={editor.duration_minutes}
                  disabled={!isDraft}
                  onChange={(event) =>
                    setEditor((current) => ({
                      ...current,
                      duration_minutes: event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Tags
                <input
                  className="bo-input"
                  value={editor.tags}
                  disabled={!isDraft}
                  onChange={(event) =>
                    setEditor((current) => ({
                      ...current,
                      tags: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="bo-span-2">
                Description
                <textarea
                  className="bo-input"
                  rows="3"
                  value={editor.description}
                  disabled={!isDraft}
                  onChange={(event) =>
                    setEditor((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="bo-span-2">
                URL externe
                <input
                  className="bo-input"
                  value={editor.url}
                  disabled={!isDraft || Boolean(detail.storage_key)}
                  onChange={(event) =>
                    setEditor((current) => ({
                      ...current,
                      url: event.target.value,
                    }))
                  }
                  placeholder={
                    detail.storage_key
                      ? 'Un fichier stocké est actuellement associé'
                      : 'https://…'
                  }
                />
              </label>
              <label className="bo-span-2">
                Contenu texte
                <textarea
                  className="bo-input"
                  rows="6"
                  value={editor.content_text}
                  disabled={!isDraft}
                  onChange={(event) =>
                    setEditor((current) => ({
                      ...current,
                      content_text: event.target.value,
                    }))
                  }
                />
              </label>
              {isDraft ? (
                <div className="bo-actions bo-span-2">
                  <button className="bo-button bo-button--primary">
                    Enregistrer les modifications
                  </button>
                </div>
              ) : (
                <div className="bo-alert bo-span-2">
                  Repassez cette ressource en brouillon pour modifier son contenu
                  ou remplacer son fichier.
                </div>
              )}
            </form>

            <div className="bo-resource-file-card">
              <div>
                <h4>Fichier & prévisualisation</h4>
                {detail.file_name ? (
                  <>
                    <strong>{detail.file_name}</strong>
                    <p className="bo-muted">
                      {detail.mime_type || 'Type inconnu'} ·{' '}
                      {formatBytes(detail.file_size_bytes)}
                    </p>
                  </>
                ) : detail.url ? (
                  <p className="bo-muted">Ressource basée sur une URL externe.</p>
                ) : detail.content_html ? (
                  <p className="bo-muted">Ressource basée sur du contenu texte.</p>
                ) : (
                  <p className="bo-muted">Aucun contenu associé pour le moment.</p>
                )}
              </div>
              <div className="bo-actions">
                {canPreview && (
                  <button
                    className="bo-button bo-button--ghost"
                    onClick={previewResource}
                  >
                    Prévisualiser
                  </button>
                )}
                <label
                  className={`bo-file-button ${
                    !isDraft ? 'is-disabled' : ''
                  }`}
                >
                  {uploading
                    ? 'Envoi…'
                    : detail.storage_key
                    ? 'Remplacer le fichier'
                    : 'Uploader un fichier'}
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.odt,.txt"
                    disabled={!isDraft || uploading}
                    onChange={(event) => uploadFile(event.target.files?.[0])}
                  />
                </label>
              </div>
            </div>

            {previewHtml && (
              <div className="bo-resource-preview">
                <div className="bo-panel__header">
                  <h4>Prévisualisation du contenu</h4>
                  <button
                    className="bo-button bo-button--ghost bo-button--compact"
                    onClick={() => setPreviewHtml('')}
                  >
                    Fermer
                  </button>
                </div>
                <iframe
                  className="bo-resource-preview__frame"
                  title={`Prévisualisation — ${detail.titre}`}
                  sandbox=""
                  srcDoc={previewHtml}
                />
              </div>
            )}

            <div className="bo-resource-meta-grid">
              <div>
                <small>Créée</small>
                <strong>{formatDate(detail.created_at)}</strong>
              </div>
              <div>
                <small>Dernière mise à jour</small>
                <strong>{formatDate(detail.updated_at)}</strong>
              </div>
              <div>
                <small>Niveau</small>
                <strong>{levelLabel(detail.level)}</strong>
              </div>
              <div>
                <small>Durée estimée</small>
                <strong>
                  {detail.duration_minutes === null ||
                  detail.duration_minutes === undefined
                    ? '—'
                    : `${detail.duration_minutes} min`}
                </strong>
              </div>
            </div>

            <button
              className="bo-button bo-button--ghost bo-resource-back-button"
              onClick={() =>
                gridRef.current?.scrollIntoView?.({
                  behavior: 'smooth',
                  block: 'start',
                })
              }
            >
              ↑ Retour au catalogue
            </button>
          </div>
        ) : (
          <div className="bo-empty">Ressource introuvable.</div>
        )}
      </section>
    </div>
  );
}
